/**
 * Module dependencies.
 */

import {
  FormGroup,
  HelpText
} from 'src/components/core/forms/fields-styled-components';

import { color, media, units } from 'src/styles/utils';
import { fetchPage } from 'src/api/app/pages/fetch-page';
import { getApiPageSlugByLocale } from 'src/core/utils/routes';
import { getHrefLangs } from 'src/core/utils/href-langs';
import { ifProp, theme } from 'styled-tools';
import { normalizeNewLine } from 'src/core/utils/strings';
import { regexes } from 'src/core/utils/regexes';
import { submitRecruitmentForm } from 'src/api/app/forms/submit-recruitment-form';
import { useForm } from 'react-hook-form';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useMutation } from 'react-query';
import { useSnackbar } from 'src/context/snackbar/context';
import { useTranslate } from 'src/core/utils/translator';
import Button from 'src/components/core/buttons/button';
import Checkbox from 'src/components/core/checkbox';
import Container from 'src/components/core/layout/container';
import Image from 'src/components/core/image';
import Input from 'src/components/core/forms/fields/input';
import Loading from 'src/components/core/loading';
import Metatags from 'src/components/metatags';
import ParallaxFadeInUp from 'src/components/core/animations/parallax-fade-in-up';
import PrivacyPolicyCheckboxLabel from 'src/components/core/forms/privacy-policy-checkbox-label';
import RawHtml from 'src/components/core/raw-html';
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';

import Type from 'src/components/core/typography';
import Upload from 'src/components/core/forms/fields/upload';
import styled, { css } from 'styled-components';
import useNavbarTheme from 'src/context/navbar-theme/use-navbar-theme';
import usePage from 'src/api/app/pages/use-page';

/**
 * `PageProps` type.
 */

type PageProps = {
  description: string,
  descriptionSeo?: string | null,
  id: string,
  media?: Array<{
    fileUrl?: string,
    id: string
  }> | null,
  secondaryDescription: string,
  secondaryTitle: string,
  slug: string,
  subtitle?: string | null,
  title: string,
  titleSeo?: string | null,
  translations: Record<string, unknown>[]
};

/**
 * `Props` type.
 */

type Props = {
  data: PageProps,
  slug: string
};

/**
 * `Section` styled component.
 */

const Section = styled.section<{ isLoading: boolean }>`
  background-color: ${color('beige200')};
  overflow: hidden;
  padding: ${theme('dimensions.navbarHeightMobile')}px 0 ${units(16.5)};
  position: relative;

  ${ifProp('isLoading', css`
    min-height: 100vh;
  `)}

  ${media.min('ms')`
    padding: ${theme('dimensions.navbarHeight')}px 0 ${units(25)};
  `}
`;

/**
 * `StyledContainer` styled component.
 */

const StyledContainer = styled(Container)`
  ${media.min('ms')`
    padding: 0;
  `}
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  display: grid;
  grid-template-areas:
    'title'
    'desc'
    'form'
    'image';
  grid-template-columns: 100%;
  grid-template-rows: repeat(4, max-content);

  ${media.min('ms')`
    grid-template-areas:
      '. . title title title .'
      '. . desc  desc  .     .'
      '. . form  .     image image'
      '. . form  .     image image';
    grid-template-columns: ${theme('grid.gutter')}px 1fr 4fr 1fr 6fr ${theme('grid.gutter')}px;
    grid-template-rows: repeat(4, max-content);
  `}
`;

/**
 * `Title` styled component.
 */

const Title = styled(Type.H1)`
  grid-area: title;
  margin-bottom: ${units(3)};
  padding-top: ${units(5)};

  ${media.min('ms')`
    margin: 0;
    padding-top: ${units(10)};
  `}
`;

/**
 * `SubTitle` styled component.
 */

const SubTitle = styled(Type.H5).attrs({
  as: 'span'
})`
  display: block;
  grid-area: subTitle;
  margin-bottom: ${units(2)};
  padding-top: ${units(1)};

  ${media.min('ms')`
    padding-top: ${units(2)};
  `}
`;

/**
 * `DescriptionWrapper` styled component.
 */

const DescriptionWrapper = styled.div`
  grid-area: desc;
`;

/**
 * `Description` styled component.
 */

const Description = styled(Type.H6).attrs({ as: 'p', small: true })`
  color: ${color('grey400')};
  margin-bottom: ${units(5)};

  ${media.min('ms')`
    margin-bottom: ${units(10)};
  `}
`;

/**
 * `SecondaryTitle` styled component.
 */

const SecondaryTitle = styled(Type.H5).attrs({ as: 'h2' })`
  font-weight: 400;
  margin-bottom: ${units(1)};
`;

/**
 * `SecondaryDescription` styled component.
 */

const SecondaryDescription = styled(Type.H6).attrs({ as: 'p', small: true })`
  color: ${color('grey400')};
  margin-bottom: ${units(6)};
`;

/**
 * `ImageWrapper` styled component.
 */

const ImageWrapper = styled.div`
  align-self: flex-start;
  grid-area: image;
  padding-bottom: 117%;
  position: relative;

  > div {
    bottom: 0;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
  }
`;

/**
 * `FormWrapper` styled component.
 */

const FormWrapper = styled.div`
  grid-area: form;

  ${media.max('ms')`
    margin-bottom: ${units(13)};
  `}
`;

/**
 * `Careers` page.
 */

const Careers = (props: Props): ReactElement => {
  const { locale, translate } = useTranslate();
  const { showMessage } = useSnackbar();
  const {
    data,
    isLoading,
    isSuccess
  } = usePage<PageProps>({
    initialData: props.data,
    slug: props.slug
  });

  const subtitle = data?.subtitle;
  const title = data?.title;
  const description = data?.description;
  const imageUrl = data?.media ? data?.media[0]?.fileUrl : null;
  const {
    clearErrors,
    control,
    errors,
    formState,
    handleSubmit,
    register,
    reset,
    setError,
    setValue
  } = useForm({ mode: 'onBlur' });

  const handleClearValue = useCallback((name: string) => {
    setValue(name, null);
  }, [setValue]);

  const { isSubmitting } = formState;
  const { mutate: submitForm } = useMutation((formData: FormData) => {
    return submitRecruitmentForm({ formData, locale });
  }, {
    onError: () => {
      showMessage(translate('forms:messages.error'), { appearance: 'error' });
    },
    onSuccess: () => {
      showMessage(translate('forms:messages.success'), { appearance: 'success' });

      reset();
    }
  });

  const sectionRef = useRef<HTMLDivElement>();
  const [isSubmitted, setIsSubmitted] = useState<boolean>();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const onSubmit = useCallback(async data => {
    const { email, file, name } = data;
    const formData = new FormData();

    formData.append('name', name);
    formData.append('email', email);
    formData.append('file', file);

    await executeRecaptcha('recruitmentForm')
      .then(token => formData.append('recaptchaToken', token));

    setIsSubmitted(true);
    submitForm(formData);
  }, [executeRecaptcha, submitForm]);

  useNavbarTheme({ theme: 'dark' });

  useEffect(() => {
    if (!isSubmitted) {
      return;
    }

    const timeout = setTimeout(() => {
      if (sectionRef.current && sectionRef.current.scrollIntoView) {
        sectionRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }

      setIsSubmitted(false);
    }, 300);

    return () => clearTimeout(timeout);
  }, [isSubmitted]);

  return (
    <>
      <Metatags
        description={data?.descriptionSeo || description}
        hrefLangs={getHrefLangs(data?.translations, 'careers')}
        pageTitle={title}
        title={data?.titleSeo}
      />

      <Section
        isLoading={isLoading}
        ref={sectionRef}
      >
        <Loading active={isLoading} />

        {isSuccess && (
          <>
            <StyledContainer>
              <Grid>
                <Title>
                  <ParallaxFadeInUp parallaxOptions={{ speed: 1 }}>
                    {`${title} `}

                    {subtitle && (
                      <SubTitle>
                        {subtitle}
                      </SubTitle>
                    )}
                  </ParallaxFadeInUp>
                </Title>

                <DescriptionWrapper>
                  <ParallaxFadeInUp parallaxOptions={{ speed: 2 }}>
                    <RawHtml element={Description}>
                      {normalizeNewLine(description)}
                    </RawHtml>
                  </ParallaxFadeInUp>
                </DescriptionWrapper>

                <FormWrapper>
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <SecondaryTitle>
                      {data?.secondaryTitle}
                    </SecondaryTitle>

                    <SecondaryDescription>
                      {data?.secondaryDescription}
                    </SecondaryDescription>

                    <Input
                      disabled={isSubmitting}
                      error={errors?.name}
                      label={translate('forms:nameLabel')}
                      name={'name'}
                      placeholder={translate('forms:namePlaceholder')}
                      ref={register<any>({
                        required: {
                          message: translate('forms:requiredField'),
                          value: true
                        }
                      })}
                    />

                    <Input
                      disabled={isSubmitting}
                      error={errors?.email}
                      label={translate('forms:emailLabel')}
                      name={'email'}
                      placeholder={translate('forms:emailPlaceholder')}
                      ref={register<any>({
                        pattern: {
                          message: translate('forms:emailPatternValidation'),
                          value: regexes.email
                        },
                        required: {
                          message: translate('forms:requiredField'),
                          value: true
                        }
                      })}
                      type={'email'}
                    />

                    <Upload
                      accept={'application/pdf'}
                      clearError={clearErrors}
                      clearValue={handleClearValue}
                      control={control}
                      disabled={isSubmitting}
                      error={errors?.file}
                      helpText={'Max. 1mb (.pdf)'}
                      // Force re-render to clear value.
                      // @ts-ignore
                      key={isSubmitted}
                      label={translate('forms:fileLabel')}
                      maxSize={1000000}
                      name={'file'}
                      rules={{ required: {
                        message: translate('forms:requiredField'),
                        value: true
                      } }}
                      setError={setError}
                      setValue={setValue}
                    />

                    <FormGroup>
                      <Checkbox
                        alignment={'flex-start'}
                        disabled={isSubmitting}
                        label={
                          <PrivacyPolicyCheckboxLabel
                            i18nKey={'forms:privacyPolicyCareersCheckbox'}
                          />
                        }
                        name={'acceptPrivacyPolicy'}
                        ref={register<any>({
                          required: {
                            message: translate('forms:privacyPolicyError'),
                            value: true
                          }
                        })}
                      />

                      {errors?.acceptPrivacyPolicy && (
                        <HelpText hasError>
                          {errors?.acceptPrivacyPolicy.message}
                        </HelpText>
                      )}
                    </FormGroup>

                    <Button
                      colorTheme={'primary'}
                      isLoading={isSubmitting}
                      type={'submit'}
                      variant={'fill'}
                    >
                      {translate('forms:submit')}
                    </Button>
                  </form>
                </FormWrapper>

                <ImageWrapper>
                  {imageUrl && (
                    <ParallaxFadeInUp
                      inViewOptions={{ threshold: 0 }}
                      parallaxOptions={{
                        percentage: 0.1,
                        speed: 2
                      }}
                    >
                      <Image
                        alt={title}
                        layout={'fill'}
                        objectFit={'cover'}
                        src={imageUrl}
                      />
                    </ParallaxFadeInUp>
                  )}
                </ImageWrapper>
              </Grid>
            </StyledContainer>
          </>
        )}
      </Section>
    </>
  );
};

/**
 * Export `getServerSideProps`.
 */

export async function getServerSideProps({ locale, query }) {
  const slug = getApiPageSlugByLocale('careers', locale);
  const pageData = await fetchPage({
    hasPreview: query?.preview,
    locale,
    slug
  });

  if (!pageData) {
    return {
      notFound: true
    };
  }

  return {
    props: {
      data: pageData,
      slug
    }
  };
}

/**
 * Export `Careers` page.
 */

export default Careers;
